import { useEffect } from "react"
import { BooksZoneType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { EditBookType, SubmitBook } from "@/types/book"
import { addBook, editBook } from "@/lib/api/book"
import { getAllZones } from "@/lib/api/zone"
import { bookSchema } from "@/lib/schema/book"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { ComboboxSearch } from "@/components/combobox-search"

import { Icons } from "../icons"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"

interface zoneData {
  zone_id: number
  zone_name: string
  client: string
}

const formSchema = z.object({
  name: z.string().min(1, "Please enter a valid name"),
  zone_id: z.number().min(0, "Please enter a valid zone"),
  description: z.string().min(1, "Enter a description"),
})
export default function BookForm({
  setShowBookZoneModal,
  editData,
}: {
  setShowBookZoneModal: React.Dispatch<React.SetStateAction<boolean>>
  editData?: BooksZoneType
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["zones"],
    queryFn: getAllZones,
  })
  const navigate = useNavigate()
  const { toast } = useToast()

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: editData?.name || "",
      zone_id: -1,
      description: editData?.description || "",
    },
  })
  const zones: Array<{
    value: string
    label: string
    id: number
  }> =
    (data?.results as zoneData[])
      ?.map((item) => ({
        value: item.zone_name,
        label: item.zone_name,
        id: item.zone_id,
      }))
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value),
      ) ?? []

  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number
      payload: SubmitBook
    }) => {
      if (id) {
        await editBook(payload, id)
      } else {
        await addBook(payload)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books-per-zone"] })
      toast({
        description: editData
          ? "Book Edited successfully"
          : "Book added successfully",
      })
      setShowBookZoneModal(false)
      navigate({ to: "/zone-settings/books-zone" })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    const payload: SubmitBook = {
      name: values.name,
      zone_id: values.zone_id,
      description: values.description,
    }

    mutation.mutate({ id: editData?.id as number, payload })
  }

  useEffect(() => {
    if (editData) {
      form.reset({
        name: editData.name || "",
        zone_id: editData.zone_id ?? -1, // Ensure zone_id is properly set
        description: editData.description || "",
      })
    }
  }, [editData, form])

  return (
    <div className="px-2">
      <Form {...form}>
        <form
          className="mx-auto grid w-full max-w-4xl grid-cols-1 gap-4"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <Card className="my-4 grid-cols-2 gap-4 p-6 md:grid">
            <div className="flex flex-col space-y-1.5">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <div>
                    <label htmlFor="name">
                      Name <span className="text-red-600">*</span>
                    </label>
                    <Input
                      id="name"
                      placeholder="Please enter a name"
                      className="w-full"
                      {...field}
                    />
                    <FormMessage />
                  </div>
                )}
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <FormField
                control={form.control}
                name="zone_id"
                render={({ field }) => (
                  <div className="flex flex-col">
                    <label htmlFor="name">
                      Zone <span className="text-red-600">*</span>
                    </label>
                    <ComboboxSearch
                      field={field}
                      options={zones}
                      placeholder="Please select a zone"
                      idAsValue
                    />
                    <FormMessage />
                  </div>
                )}
              />
            </div>
            <div className="col-start-1 col-end-3 mb-4 space-y-1.5">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <div className="flex flex-col">
                    <label htmlFor="name">
                      Description <span className="text-red-600">*</span>
                    </label>
                    <Textarea placeholder="Description..." {...field} />
                    <FormMessage />
                  </div>
                )}
              />
            </div>
          </Card>
          <div className="col-span-2 flex w-full justify-end gap-3">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowBookZoneModal(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            {editData ? (
              <Button className="w-28" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Save Changes
              </Button>
            ) : (
              <Button className="w-28" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Add
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  )
}
